<template>
  <StickyFooter :sticky="canContinue">
    <template #header>
      <section class="hero is-info">
        <div class="loader-hero-body">
          <span class="loader-hero-title">Build Datasets</span>
          <ul class="loader-step-checklist">
            <li
              class="mr-2"
              :class="{ 'step-done': collectionIds.length >= 1 }"
            >
              Create a collection
              <span
                class="icon has-text-primary"
                :class="{
                  'hoverable-icon': !showHelp['bldr-collections'],
                }"
                @click="toggleHelp('bldr-collections')"
              >
                <i class="fa fa-question fa-xs" />
              </span>
            </li>
            <li class="mr-2" :class="{ 'step-done': hasMultiDimensionDataset }">
              (optional) Assemble multi-dimensions datasets
              <span
                class="icon has-text-primary"
                :class="{
                  'hoverable-icon': !showHelp['bldr-datasets'],
                }"
                @click="toggleHelp('bldr-datasets')"
              >
                <i class="fa fa-question has-text-primary fa-xs" />
              </span>
            </li>
            <li class="mr-2" :class="{ 'step-done': datasetsCount > 0 }">
              Stage datasets
            </li>
          </ul>
        </div>
      </section>
    </template>
    <template #body>
      <div class="notification-container is-flex-direction-column m-2">
        <div
          v-if="showHelp['bldr-collections']"
          class="notification is-info m-1"
        >
          <button
            class="delete"
            @click="setShowHelp({ key: 'bldr-collections', value: false })"
          ></button>
          <span class="icon is-large has-text-primary">
            <i class="fa fa-info" />
          </span>
          <div class="mr-6" style="text-align: left">
            <div class="is-size-6 has-text-weight-bold pb-2">
              Datasets are grouped into collections
            </div>
            <ul style="list-style: none">
              <li class="is-size-7 pb-2">
                When registering many datasets, it is advised to group datasets
                of similar types into different
                <i>collections</i>
              </li>
              <li class="is-size-7 pb-2">
                In particular, this allows for separating the assay's
                <b>raw data</b>
                e.g. the raw reads (FastQ format) of a sequencing assay from
                <b>derived data</b>
                e.g. the aligned reads (BAM format).
              </li>
              <li class="is-size-7 pb-2">
                Note that assays must have raw datasets that have to be
                associated to samples (
                <em>Assign samples</em>
                step of this wizard) while
                <b>non raw data</b>
                may be associated to samples at user discretion.
              </li>
            </ul>
          </div>
          <div
            class="is-flex m-2"
            style="position: absolute; right: 0; bottom: 0"
            @click="setShowHelp({ key: 'bldr-collections', value: false })"
          >
            <button class="button is-small is-info is-inverted is-outlined">
              OK
            </button>
          </div>
        </div>
        <div v-if="showHelp['bldr-datasets']" class="notification is-info m-1">
          <button
            class="delete"
            @click="setShowHelp({ key: 'bldr-datasets', value: false })"
          ></button>
          <span class="icon is-large has-text-primary">
            <i class="fa fa-info" />
          </span>

          <div class="mr-6" style="text-align: left">
            <div class="is-size-6 has-text-weight-bold pb-2">
              Multiple datasets can be assembled into "multi-dimensions"
              datasets (optional)
            </div>
            <ul style="list-style: none">
              <li class="is-size-7 pb-2">
                Certain datasets are inherently connected and need to be
                assembled together. This is for example the case for the read 1
                and read 2 obtained with paired-end sequencing.
              </li>
              <li class="is-size-7 pb-2">
                Use the
                <b>dataset builder</b>
                to assemble datasets together. When a dataset is ready,
                <u>stage it</u>
                , it will appear in the
                <b>staged datasets</b>
                panel below.
              </li>
              <li class="is-size-7 pb-2">
                Once staged, the programmatically generated name of the dataset
                can be modified if needed.
              </li>
            </ul>
          </div>
          <div
            class="is-flex m-2"
            style="position: absolute; right: 0; bottom: 0"
            @click="setShowHelp({ key: 'bldr-datasets', value: false })"
          >
            <button class="button is-small is-info is-inverted is-outlined">
              OK
            </button>
          </div>
        </div>
      </div>
      <div class="padded-container" style="margin: 1rem 0">
        <DefaultPanel
          :key="`builderlist-${
            remainingFilesCount === 0 ? 'collapsed' : 'opened'
          }`"
          :collapsed="datasetFileIds.length > 0 && remainingFilesCount === 0"
        >
          <template #header>
            <div class="panel-header">
              <div>Dataset Builder</div>
              <div class="panel-header-tags">
                <ul class="tag-list has-text-weight-bold">
                  <li class="pl-1 pr-1">
                    <span class="tag is-info">
                      <span class="icon pr-1">
                        <i class="fa-regular fa-file fa-sm" />
                      </span>
                      {{ filesList.length }}
                      {{
                        datasetFileIds.length === 0
                          ? pluralize('file', filesList.length)
                          : ''
                      }}
                      selected
                    </span>
                  </li>
                  <template v-if="datasetFileIds.length">
                    <li class="pl-1 pr-1">
                      <span class="tag is-success">
                        <span class="icon">
                          <i class="fa-regular fa-file fa-sm" />
                        </span>
                        <span>{{ datasetFileIds.length }} in datasets</span>
                        <span v-if="remainingFilesCount === 0" class="icon">
                          <i class="fa fa-check-double fa-sm" />
                        </span>
                      </span>
                    </li>
                    <li v-if="remainingFilesCount" class="pl-1 pr-1">
                      <span class="tag is-warning">
                        <span class="icon pr-1">
                          <i class="fa-regular fa-file fa-sm" />
                        </span>
                        {{ remainingFilesCount }} not assigned
                      </span>
                    </li>
                  </template>
                </ul>
              </div>
            </div>
          </template>
          <CollectionOrganiser
            v-if="filesCount > 0"
            class="mb-2"
            :can-edit="true"
            :show-count="true"
            :remaining-files-count="remainingFilesCount"
            :selected-collection-id="selectedCollectionId"
          >
            <template #collection>
              <CollectionBuilder
                :key="`collbuild-${selectedCollectionId}`"
                :selected-collection-id="selectedCollectionId"
                :can-change-raw-data="registerToAssay"
                :assay-id="assayId"
                :assay-type="assayType"
                :assay-technology="assayTechnology"
                :remaining-files-list="remainingFilesList"
                :remaining-files-count="remainingFilesCount"
                class="collection-container"
              ></CollectionBuilder>
            </template>
          </CollectionOrganiser>
          <section v-else class="hero is-info">
            <div class="hero-body">
              <p class="subtitle">Start by selecting some files</p>
            </div>
          </section>
        </DefaultPanel>
        <DefaultPanel
          v-show="datasetsList.length > 0"
          :key="`stagedlist-sel${selectedCollectionId}${
            !collectionsIdsWithStagedDatasets.includes(selectedCollectionId)
              ? 'collapsed'
              : 'opened'
          }`"
          style="margin-top: 2rem"
          :collapsed="
            !datasetsList.length > 1 ||
            !collectionsIdsWithStagedDatasets.includes(selectedCollectionId)
          "
        >
          <template #header>
            <div class="panel-header">
              <div>Staged Datasets</div>
              <div class="panel-header-tags">
                <ul class="tag-list has-text-weight-bold">
                  <li class="pl-1 pr-1">
                    <span class="tag is-info">
                      {{ collectionsIdsWithStagedDatasets.length }}
                      {{
                        pluralize(
                          'collection',
                          collectionsIdsWithStagedDatasets.length,
                        )
                      }}
                    </span>
                  </li>
                  <li class="pl-1 pr-1">
                    <span class="tag is-success">
                      <span class="icon pr-2">
                        <i class="fa-regular fa-copy fa-sm" />
                      </span>
                      {{ datasetsList.length }}
                      {{ pluralize('dataset', datasetsList.length) }}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </template>
          <CollectionOrganiser
            v-if="collectionsIdsWithStagedDatasets.length > 0"
            class="mb-2"
            :auto-init="datasetsList.length === 0"
            :show-dataset-count="true"
            :remaining-files-count="remainingFilesCount"
            :selected-collection-id="selectedCollectionId"
          >
            <template #collection>
              <StagedDatasetViewer
                v-if="
                  collectionsIdsWithStagedDatasets.includes(
                    selectedCollectionId,
                  )
                "
                :collection-id="selectedCollectionId"
              />
            </template>
          </CollectionOrganiser>
          <div v-else>
            <section class="hero is-info">
              <div class="hero-body has-text-center">
                <p class="title is-size-6">No dataset has yet been staged</p>
                <p class="subtitle is-size-7">
                  Use the dataset builder and stage the first dataset
                </p>
              </div>
            </section>
          </div>
        </DefaultPanel>
      </div>
    </template>
    <template #footer>
      <div
        v-if="missingRawData"
        class="is-size-7 has-text-weight-bold has-text-danger pb-3"
      >
        <i class="fa fa-caret-right" />
        Staging at least one collection of raw data is needed to continue
      </div>
      <div
        v-if="remainingFilesCount"
        class="has-text-weight-bold has-text-warning pb-5"
      >
        <i class="fa fa-caret-right" />
        {{ remainingFilesCount }}
        {{ pluralize('file', remainingFilesCount) }} are not yet used in any
        dataset collection ({{ filesList.length }}
        {{ pluralize('file', filesList.length) }} selected,
        {{ datasetFileIds.length }} used in datasets)
      </div>
      <p v-if="autoStaged" class="help mb-3">
        Your single dataset was auto staged. You can continue
      </p>
      <router-link
        v-tooltip="{
          content: continueButtonMsg,
          html: true,
        }"
        :to="$attrs.nextRoute"
      >
        <BaseButton
          :variant="remainingFilesCount > 0 ? 'warning' : 'primary'"
          :outlined="!canContinue"
          :disabled="!canContinue"
          :icon="['fa', 'fa-step-forward']"
          label="Continue"
        />
      </router-link>
    </template>
  </StickyFooter>
</template>

<script>
import pluralize from 'pluralize'
import { some } from 'lodash-es'
import { mapGetters, mapActions, mapState, mapMutations } from 'vuex'
import CollectionOrganiser from '@/components/data-loader/CollectionOrganiser.vue'
import CollectionBuilder from '@/components/data-loader/CollectionBuilder.vue'
import StagedDatasetViewer from '@/components/data-loader/StagedDatasetViewer.vue'
import DefaultPanel from '@/components/DefaultPanel.vue'
import StickyFooter from '@/components/data-loader/StickyFooter.vue'
// import { getDropboxFolderContent } from '@/utils/api/dataManagement'

export default {
  name: 'DatasetBuilderView',
  emits: ['is-loading'],
  components: {
    CollectionOrganiser,
    CollectionBuilder,
    StagedDatasetViewer,
    DefaultPanel,
    StickyFooter,
  },
  data() {
    return {
      autoStaged: false,
      openHelp: false,
      openHelpCollection: true,
    }
  },
  computed: {
    ...mapState('dataloader/main', ['showHelp']),
    ...mapState('dataloader/assay', {
      assayId: (state) => state.assayData?.id?.value || null,
      registerToAssay: 'registerToAssay',
    }),
    ...mapGetters('dataloader/assay', {
      assayType: 'resolvedAssayType',
      assayTechnology: 'assayTechnology',
      assayHasRegisteredData: 'assayHasRegisteredData',
      enforceRawData: 'enforceRawData',
    }),
    ...mapState('dataloader/collections', [
      'collections',
      'selectedCollectionId',
    ]),
    ...mapGetters('dataloader/collections', [
      'collectionIds',
      'collectionsIdsWithStagedDatasets',
    ]),
    ...mapGetters('dataloader/datasets', [
      'remainingFilesCount',
      'datasetsList',
      'datasetsListsByCollectionId',
      'datasetFileIds',
      'hasStagedRawData',
      'remainingFilesList',
    ]),
    ...mapGetters('dataloader/dropbox', ['filesList', 'filesCount']),

    isAutoStageNeeded() {
      return (
        this.filesList.length === 1 &&
        this.datasetsList.length === 0 &&
        !this.assayHasRegisteredData
      )
    },
    missingDataCollectionNames() {
      return this.collectionIds
        .filter(
          (id) => (this.datasetsListsByCollectionId[id]?.length || 0) === 0,
        )
        .map((id) => this.collections[id].name)
    },
    missingRawData() {
      return this.enforceRawData && !this.hasStagedRawData
    },
    canContinue() {
      return (
        this.filesCount > 0 &&
        !this.missingDataCollectionNames.length > 0 &&
        !this.missingRawData
      )
    },
    continueButtonMsg() {
      const missingDataInCollecMsg = `Some collection (${this.missingDataCollectionNames.join(
        ', ',
      )}) do not contain any staged data.`
      const remainingFileMsg =
        'Some files have been loaded but not assigned to a dataset.'
      const missingRawMsg = 'Raw data is expected but none has been staged.'
      return [
        this.missingRawData ? missingRawMsg : null,
        this.missingDataCollectionNames.length > 0
          ? missingDataInCollecMsg
          : null,
        this.remainingFilesCount > 0 ? remainingFileMsg : null,
      ]
        .filter((m) => m !== null)
        .map((m) => `<p>${m}</p>`)
        .join('')
    },
    datasetsCount() {
      return this.datasetsList.length || 0
    },
    hasMultiDimensionDataset() {
      return some(this.datasetsList, (item) => item && item.length > 1)
    },
  },
  methods: {
    pluralize,
    ...mapMutations('dataloader/main', {
      setShowHelp: 'SET_SHOW_HELP',
      toggleHelp: 'TOGGLE_HELP',
    }),
    ...mapActions('dataloader/datasets', ['addDatasets', 'cleanupDatasets']),
    autoStage() {
      const onlyFile = this.filesList[0]
      const { id, name } = onlyFile
      // Use the first collection if available
      const collectionId = this.collectionIds[0] // <<< MIGHT NEED TO WATCH FOR COLLECTIONS TO BE INITIALISED
      if (collectionId) {
        this.addDatasets({
          collectionId,
          datasets: [{ id, name, files: [onlyFile] }],
        })
        this.autoStaged = true
      } else {
        console.error('NEED AUTO STAGE BUT FIRST COLLECTION NOT BE READY')
      }
    },
  },
  watch: {
    datasetsCount: function (newCount, oldCount) {
      if (oldCount === 0 && newCount > 0) {
        this.openHelp = false
        this.openHelpCollection = false
      }
    },
  },
  beforeMount() {
    this.$emit('is-loading', true)
  },
  async mounted() {
    await this.cleanupDatasets()
    // Auto-stage logic remains here, but first collection selection
    // is now handled in CollectionOrganiser
    if (this.isAutoStageNeeded) this.autoStage()
    this.$emit('is-loading', false)
  },
}
</script>

<style scoped>
.panel {
  margin-bottom: 1rem;
}
.detail-container {
  height: 100%;
}
.panel-header {
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  flex-wrap: wrap;
}
.panel-header .panel-header-tags {
  display: flex;
  align-items: center;
  padding: 0 1rem;
}
.panel-header-tags .tag-list {
  display: flex;
  align-items: center;
  line-height: 20px;
}
</style>
